import {  useParams, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import Dialog from "components/organisms/Dialog/Dialog";
import { getdraftQuotation } from "api/services/quotation";
import { modifyDraftData, omitScheduleStops } from "helpers/quotationFormHelper";

interface IQuotationConfirmDialogProps {
  isOpen: boolean;
  close: () => void;
  setShowReuseFormModal: Function;
  referenceId: string;
}

function QuotationConfirmDialog(props: IQuotationConfirmDialogProps) {
  const { isOpen, close, setShowReuseFormModal, referenceId } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const shipmentId: any = useParams()?.id?.toUpperCase();

  const intl = useIntl();
  const requestFormMethods = useFormContext();
  const methods = useForm();
  const { handleSubmit, reset } = methods;

  const getLatestQuotationDraft = (data: any) => {
    const selectedSection = Object.keys(data)
      .filter((key) => data[key])
      .join(",");
    getdraftQuotation(referenceId, shipmentId, {
      params: {
        section: selectedSection,
      },
    })
      .then((res) => {
        const existingData = { ...res.data.data };
        const updatedData = omitScheduleStops({
          ...existingData,
          schedule: {
            ...existingData.schedule,
            ...data.schedule,
          },
        });
        searchParams.set("draft_id", res.data.reference_number);
        setSearchParams(searchParams);
        requestFormMethods.reset(modifyDraftData(updatedData));
        reset();
        close();
      })
      .catch((error) => {
        errorToast(error);
      });
  };

  return (
    <Dialog
      title={<FormattedMessage id="quotation-form.template.latestQuotation" defaultMessage="Latest quotations" />}
      show={isOpen}
      onHide={close}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(getLatestQuotationDraft)}>
          <FormattedMessage
            id="quotation-form.template.quotationReuseTitle"
            defaultMessage="Please select from the most recent quotations to reuse."
          />
          <div className="mt-5 d-flex flex-column" style={{ gap: "20px" }}>
            <Checkbox
              providerSections
              checkboxLabel={intl.formatMessage({
                id: "quote-request-detail.service",
                defaultMessage: "Service",
              })}
              name="service"
            />
            <Checkbox
              providerSections
              checkboxLabel={intl.formatMessage({
                id: "general.schedule",
                defaultMessage: "Schedule",
              })}
              name="schedule"
            />
            <Checkbox
              providerSections
              checkboxLabel={intl.formatMessage({
                id: "requestApproval.costs",
                defaultMessage: "Costs",
              })}
              name="costs"
            />
            <Checkbox
              providerSections
              checkboxLabel={intl.formatMessage({
                id: "shipment-quotes.components.quotes.serviceConfirmation",
                defaultMessage: "Service confirmation",
              })}
              name="service_confirmation"
            />
            <Checkbox
              providerSections
              checkboxLabel={intl.formatMessage({
                id: "quote-request-detail.additionalInformation",
                defaultMessage: "Additional information",
              })}
              name="additional_information"
            />
          </div>
          <div className="d-flex justify-content-between mt-5">
            <Button
              variant="outline-primary"
              onClick={() => {
                setShowReuseFormModal(true);
              }}
              label={intl.formatMessage({
                id: "quotation-form.template.backToRecentQuote",
                defaultMessage: "Back to the recent quotes",
              })}
              data-testid={testIds.QUOTATION_FORM_TEMPLATE_BACK_TO_QUOTES}
            />
            <Button
              variant="primary"
              label={intl.formatMessage({
                id: "quotation-form.template.reuseQuotation",
                defaultMessage: "Reuse quotation",
              })}
              type="submit"
              data-testid={testIds.QUOTATION_FORM_TEMPLATE_REUSE_QUOTATION_BUTTON}
            />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default QuotationConfirmDialog;
